import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { EyeOff, Eye, Trash2, ExternalLink, CalendarCheck, CalendarX, Info, Zap } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import AdminConfirm from '@/components/AdminConfirm';
import AdminForceEndModal from '@/components/admin/AdminForceEndModal';
import { FilterBar, SelectPill, Pagination, StateMsg } from '@/components/AdminFilters';

const PLATFORM_OPTS = [
  { value: 'all', label: '모든 플랫폼' },
  { value: 'tiktok', label: '틱톡' }, { value: 'instagram', label: '인스타그램' },
  { value: 'youtube', label: '유튜브' }, { value: 'naver', label: '네이버' },
  { value: 'kakao', label: '카카오' }, { value: 'others', label: '기타' },
];
const CATEGORY_OPTS = [
  { value: 'all', label: '모든 카테고리' }, { value: 'fashion', label: '패션' },
  { value: 'beauty', label: '뷰티' }, { value: 'food', label: '식품' }, { value: 'pet', label: '펫' },
  { value: 'kids', label: '키즈' }, { value: 'living', label: '리빙' }, { value: 'others', label: '기타' },
];
const STATUS_OPTS = [
  { value: 'all', label: '모든 상태' }, { value: 'scheduled', label: '예정' },
  { value: 'live', label: 'LIVE' }, { value: 'ended', label: '종료' },
];
const VIS_OPTS = [
  { value: 'all', label: '모든 공개상태' }, { value: 'public', label: '공개' },
  { value: 'private', label: '비공개' }, { value: 'hidden', label: '숨김' },
];
const DATE_OPTS = [
  { value: 'all', label: '전체 기간' }, { value: 'today', label: '오늘' }, { value: 'week', label: '이번 주' },
];

const AdminBroadcasts = ({ user }) => {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [seller, setSeller] = useState(params.get('seller') || '');
  const [platform, setPlatform] = useState(params.get('platform') || 'all');
  const [category, setCategory] = useState(params.get('category') || 'all');
  const [status, setStatus] = useState(params.get('status') || 'all');
  const [visibility, setVis] = useState(params.get('visibility') || 'all');
  const [date, setDate] = useState(params.get('date') || 'all');
  const [page, setPage] = useState(Number(params.get('page') || 1));
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  // STEP LIVE-PUBLISHER-GUARD-1 · §4 긴급 종료 모달 target broadcast.
  const [forceEndTarget, setForceEndTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/broadcasts', {
        params: {
          q: q || undefined, seller: seller || undefined,
          platform: platform !== 'all' ? platform : undefined,
          category: category !== 'all' ? category : undefined,
          status: status !== 'all' ? status : undefined,
          visibility: visibility !== 'all' ? visibility : undefined,
          date: date !== 'all' ? date : undefined,
          page, page_size: 20,
        },
      });
      setData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
    finally { setLoading(false); }
  }, [q, seller, platform, category, status, visibility, date, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = {};
    if (q) p.q = q; if (seller) p.seller = seller;
    if (platform !== 'all') p.platform = platform; if (category !== 'all') p.category = category;
    if (status !== 'all') p.status = status; if (visibility !== 'all') p.visibility = visibility;
    if (date !== 'all') p.date = date; if (page > 1) p.page = String(page);
    setParams(p, { replace: true });
  }, [q, seller, platform, category, status, visibility, date, page, setParams]);

  const reset = () => { setQ(''); setSeller(''); setPlatform('all'); setCategory('all'); setStatus('all'); setVis('all'); setDate('all'); setPage(1); };
  const run = async (fn) => { try { const r = await fn(); toast.success(r.data?.message || '완료되었습니다.'); load(); } catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); } };

  const hide = (b) => run(() => api.post(`/admin/broadcasts/${b.schedule_id}/hide`));
  const unhide = (b) => run(() => api.post(`/admin/broadcasts/${b.schedule_id}/unhide`));
  const toggleFeatured = (b) => run(() => api.put(`/admin/broadcasts/${b.schedule_id}`, { home_schedule_featured: !b.home_schedule_featured }));
  const toggleScheduleVisible = (b) => {
    const next = !(b.schedule_visible !== false);
    return run(() => api.put(`/admin/broadcasts/${b.schedule_id}/schedule-visibility`, { schedule_visible: next }));
  };
  const del = (b) => setConfirm({
    title: '방송을 삭제하시겠습니까?',
    message: `"${b.title}" 방송은 소비자 화면과 편성표에서 즉시 사라집니다.`,
    confirmLabel: '삭제', tone: 'danger',
    onConfirm: () => run(() => api.delete(`/admin/broadcasts/${b.schedule_id}`)),
  });

  const items = data.items || [];

  return (
    <AdminLayout user={user} active="broadcasts">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>방송 관리</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">방송 검색 · 숨김 · 삭제 · 추천 지정 등을 관리합니다.</p>
      </div>

      <FilterBar q={q} setQ={setQ} onSearch={() => { setPage(1); load(); }} onReset={reset} placeholder="방송 제목 검색">
        <input value={seller} onChange={(e) => setSeller(e.target.value)} placeholder="판매자명"
          className="px-3 py-2 border rounded-full bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-sm" data-testid="filter-seller" />
        <SelectPill value={platform}   onChange={(v) => { setPlatform(v); setPage(1); }}   options={PLATFORM_OPTS} testid="filter-platform" />
        <SelectPill value={category}   onChange={(v) => { setCategory(v); setPage(1); }}   options={CATEGORY_OPTS} testid="filter-category" />
        <SelectPill value={status}     onChange={(v) => { setStatus(v); setPage(1); }}     options={STATUS_OPTS}   testid="filter-status" />
        <SelectPill value={visibility} onChange={(v) => { setVis(v); setPage(1); }}        options={VIS_OPTS}      testid="filter-visibility" />
        <SelectPill value={date}       onChange={(v) => { setDate(v); setPage(1); }}       options={DATE_OPTS}     testid="filter-date" />
      </FilterBar>

      <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-xl overflow-hidden" data-testid="admin-broadcasts-table">
        <StateMsg loading={loading} empty={!loading && items.length === 0} />
        {!loading && items.length > 0 && (
          <>
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-black/5 dark:bg-white/5 text-xs uppercase tracking-wider text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-2">방송</th>
                  <th className="px-4 py-2">판매자</th>
                  <th className="px-4 py-2">시간</th>
                  <th className="px-4 py-2">플랫폼 / 카테고리</th>
                  <th className="px-4 py-2">공개</th>
                  <th className="px-4 py-2">편성표</th>
                  <th className="px-4 py-2">추천</th>
                  <th className="px-4 py-2 text-right">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 dark:divide-white/10">
                {items.map((b) => (
                  <tr key={b.schedule_id} data-testid={`bcast-row-${b.schedule_id}`} className="hover:bg-black/5 dark:hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="font-bold truncate max-w-xs">{b.title}</div>
                      <div className="text-[11px] text-gray-500">{b.status}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <Link to={`/seller/${b.seller_id}`} target="_blank" className="inline-flex items-center gap-1 hover:underline">{b.seller_name} <ExternalLink size={10} /></Link>
                    </td>
                    <td className="px-4 py-3 text-xs">{b.scheduled_at ? format(new Date(b.scheduled_at), 'M.d HH:mm', { locale: ko }) : '-'}</td>
                    <td className="px-4 py-3 text-xs">{b.platform} · {b.category}
                      {typeof b.linked_product_count === 'number' && (
                        <div className="text-[10px] text-gray-500 mt-0.5" data-testid={`bcast-product-count-${b.schedule_id}`}>
                          연결 상품 {b.linked_product_count}개
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {b.admin_hidden ? <span className="text-red-600 font-bold">숨김</span> : (b.is_public === false ? '비공개' : '공개')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleScheduleVisible(b)}
                          data-testid={`bcast-schedule-toggle-${b.schedule_id}`}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-black border transition-colors ${
                            b.schedule_visible !== false
                              ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/20'
                              : 'bg-gray-500/10 text-gray-500 border-gray-400/30 hover:bg-gray-500/20'
                          }`}
                          title={b.schedule_visible !== false ? '편성표 노출 중' : '편성표 미노출'}
                        >
                          {b.schedule_visible !== false ? <><CalendarCheck size={12} /> 노출 중</> : <><CalendarX size={12} /> 미노출</>}
                        </button>
                        {b.schedule_visible !== false && (b.visibility_reasons || []).length > 0 && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] text-amber-600"
                            title={`편성표 노출이 설정되었지만 현재 방송 상태 또는 날짜 조건으로 인해 소비자 편성표에는 표시되지 않습니다.\n제외 사유: ${(b.visibility_reasons || []).join(', ')}`}
                            data-testid={`bcast-blocked-${b.schedule_id}`}
                          >
                            <Info size={11} /> 노출 조건 부족
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <button onClick={() => toggleFeatured(b)} className={`text-xs font-bold ${b.home_schedule_featured ? 'text-[#FF3B30]' : 'text-gray-400'}`} data-testid={`bcast-featured-${b.schedule_id}`}>
                        {b.home_schedule_featured ? '★ 추천' : '☆ 지정'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2 items-center">
                        {/* STEP LIVE-PUBLISHER-GUARD-1 · §4 긴급 종료 — LIVE/라이브 상태에만 노출.
                            일반 종료 기능을 대체하지 않음 (§5). */}
                        {/* LIVE-ADMIN-EMERGENCY-END-FIX-1 · 방송 상태와 별개로 미종료 세션(DB 기록) 있으면 노출. */}
                        {b.open_session_count > 0 && (
                          <span className="text-[10px] font-bold text-red-600" title="DB 세션 기록 기준 · 실제 Room 상태는 종료 실행 시 확인"
                                data-testid={`bcast-open-sessions-${b.schedule_id}`}>
                            세션 {b.open_session_count}{b.live_session_count ? ` (LIVE ${b.live_session_count})` : ''}
                          </span>
                        )}
                        {(b.status === 'LIVE' || b.status === '라이브' || b.status === 'live' || b.open_session_count > 0) && (
                          <button type="button" onClick={() => setForceEndTarget(b)}
                                   className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-[11px] font-black shadow-sm"
                                   title="관리자 긴급 종료 (사유 입력 필수 · 감사 로그 기록)"
                                   data-testid={`bcast-force-end-${b.schedule_id}`}>
                            <Zap size={11} /> 긴급 종료
                          </button>
                        )}
                        {b.admin_hidden ? (
                          <button onClick={() => unhide(b)} className="text-emerald-600 hover:underline text-xs font-bold" data-testid={`bcast-unhide-${b.schedule_id}`}><Eye size={12} className="inline" /> 숨김 해제</button>
                        ) : (
                          <button onClick={() => hide(b)} className="text-amber-600 hover:underline text-xs font-bold" data-testid={`bcast-hide-${b.schedule_id}`}><EyeOff size={12} className="inline" /> 숨김</button>
                        )}
                        <button onClick={() => del(b)} className="text-red-600 hover:underline text-xs font-bold" data-testid={`bcast-delete-${b.schedule_id}`}><Trash2 size={12} className="inline" /> 삭제</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="md:hidden divide-y divide-black/5 dark:divide-white/10">
              {items.map((b) => (
                <div key={b.schedule_id} className="px-4 py-3 space-y-1" data-testid={`bcast-card-${b.schedule_id}`}>
                  <div className="font-bold text-sm truncate">{b.title}</div>
                  <div className="text-xs text-gray-500">{b.seller_name} · {b.platform} · {b.category}</div>
                  <div className="text-[11px] text-gray-400">{b.scheduled_at?.slice(0, 16)} · {b.admin_hidden ? '숨김' : (b.is_public === false ? '비공개' : '공개')}</div>
                  <div className="flex gap-2 pt-1">
                    {b.admin_hidden
                      ? <button onClick={() => unhide(b)} className="text-emerald-600 text-xs font-bold">숨김 해제</button>
                      : <button onClick={() => hide(b)} className="text-amber-600 text-xs font-bold">숨김</button>}
                    <button onClick={() => del(b)} className="text-red-600 text-xs font-bold ml-auto">삭제</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Pagination page={page} total={data.total} pageSize={20} onChange={setPage} />
      <AdminConfirm state={confirm} onClose={() => setConfirm(null)} />
      <AdminForceEndModal broadcast={forceEndTarget}
                           onClose={() => setForceEndTarget(null)}
                           onEnded={() => { setForceEndTarget(null); load(); }} />
    </AdminLayout>
  );
};

export default AdminBroadcasts;
